import { CommonModule } from "@angular/common";
import {
  Component,
  Inject,
  OnInit,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import {
  ReactiveFormsModule,
  FormsModule,
  FormControl,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatOptionModule } from "@angular/material/core";
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialog,
} from "@angular/material/dialog";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { ExpeditionConditionService } from "../../master/expedition-condition/expedition-condition.service";
import {
  ExpeditionConditionAttributes,
  OrderAttributes,
} from "@albatrosdeveloper/ave-models-package";
import { MatSnackBar } from "@angular/material/snack-bar";
import { IUpdateOrder } from "./interfaces/update-order.interface";
import { UpdateOrderService } from "./update-order.service";
import AddressAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/common/embedded/address/address.entity";
import { BusinessPartnerAddressService } from "../../business-partners/business-partner/business-partner-address-modal/business-partner-address.component.service";
import { StatusEnum } from '@albatrosdeveloper/ave-models-package/lib/enums';
import { DocumentTypeEnum } from "app/common/enums/document-types.enum";

@Component({
  selector: "app-update-order-modal",
  styleUrl: "./update-order.component.scss",
  templateUrl: "./update-order.component.html",
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatTooltipModule,
    MatExpansionModule,
    MatDatepickerModule,
    MatButtonModule,
    MatIconModule,
    FormsModule,
    MatSelectModule,
  ],
})
export class UpdateOrderComponent implements OnInit {
  DocumentTypeEnum = DocumentTypeEnum;
  isLoading: boolean = false;

  expeditionConditionControl = new FormControl(null, Validators.required);
  expeditionConditionRequired: boolean = true;
  expeditionConditionDisabled: boolean = false;
  expeditionConditionList: ExpeditionConditionAttributes[] = [];
  filteredExpeditionConditionList = this.expeditionConditionList;
  searchExpeditionCondition: string = null;


  deliveryDateToUpdate = new FormControl(null);
  loadDateToUpdate = new FormControl(null);
  purchaseOrderToUpdate = new FormControl('');
  addressControl = new FormControl(null);
  addressRequired: boolean = false;
  addressDisabled: boolean = false;
  addressList: AddressAttributes[] = [];
  filteredAddressList = this.addressList;
  searchAddress: string = null;

  noteControl = new FormControl(null);
  noteRequired: boolean = false;
  noteDisabled: boolean = false;

  @ViewChild("orderConfirmationDialog")
  orderConfirmationDialog!: TemplateRef<any>;

  constructor(
    private readonly updateOrderService: UpdateOrderService,
    private readonly expeditionConditionService: ExpeditionConditionService,
    private readonly addressService: BusinessPartnerAddressService,
    public readonly dialogRef: MatDialogRef<UpdateOrderComponent>,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: OrderAttributes
  ) {
    if (data.note) {
      this.noteControl.setValue(data.note);
    }
    this.deliveryDateToUpdate.setValue(data.deliveryDateTime);
    this.loadDateToUpdate.setValue(data.loadDateTime);
    this.purchaseOrderToUpdate.setValue(data.purchaseOrder);
  }

  ngOnInit(): void {
    this.fetchExpeditionConditions();
    this.fetchAddresses();
  }

  fetchExpeditionConditions() {
    this.expeditionConditionService.findToComboBox().subscribe({
      next: (res) => {
        this.expeditionConditionList = res.data;
        this.filteredExpeditionConditionList = this.expeditionConditionList;

        // Seleccionar el actual
        if (this.data.expeditionCondition) {
          const expeditionConditionFound = this.expeditionConditionList.find(
            (e) => e.code == this.data.expeditionCondition.code
          );
          if (expeditionConditionFound) {
            this.expeditionConditionControl.setValue(
              expeditionConditionFound._id
            );
          }
        }
      },
      error: (err) => {
        this.snackBar.open(
          err.error.message ||
          "Error al obtener las condiciones de expedición.",
          "Cerrar",
          { duration: 3000 }
        );
      },
    });
  }

  filterExpeditionConditions() {
    const filterValue = this.searchExpeditionCondition.toLowerCase();
    this.filteredExpeditionConditionList = this.expeditionConditionList.filter(
      (e) =>
        e.code.toLowerCase().includes(filterValue) ||
        e.name.toLowerCase().includes(filterValue)
    );
  }

  fetchAddresses() {
    if (!this.data.shipTo) return;
    this.addressService
      .findByBusinessId(String(this.data.shipTo._id))
      .subscribe({
        next: (res) => {
          this.addressList = res.data.filter(
            (a) => a.statusId == StatusEnum.ACTIVE && a._deleted != true
          );
          this.filteredAddressList = this.addressList;

          // Seleccionar el actual
          if (this.data.address) {
            const addressFound = this.addressList.find(
              (a) => String(a._id) == String(this.data.address._id)
            );
            if (addressFound) {
              this.addressControl.setValue(addressFound._id);
            }
          }
        },
        error: (err) => {
          this.snackBar.open(
            err.error.message || "Error al obtener las direcciones.",
            "Cerrar",
            { duration: 3000 }
          );
        },
      });
  }

  filterAddresses() {
    const filterValue = this.searchAddress.toLowerCase();
    this.filteredAddressList = this.addressList.filter(
      (e) =>
        (e.externalKeys &&
          e.externalKeys.code.toLowerCase().includes(filterValue)) ||
        e.address.toLowerCase().includes(filterValue)
    );
  }

  openConfirmationDialog(): void {
    this.dialog
      .open(this.orderConfirmationDialog, {
        data: null,
        panelClass: "form-modal",
        disableClose: true,
      })
      .afterClosed()
      .subscribe((result) => result && this.onSave());
  }

  onSave(): void {
    this.isLoading = true;
    const dataToUpdate: IUpdateOrder = {
      expeditionConditionId: String(this.expeditionConditionControl.value),
      addressId: this.addressControl.value
        ? String(this.addressControl.value)
        : null,
      note: this.noteControl.value || null,
      deliveryDateToUpdate: this.convertToUTC(this.deliveryDateToUpdate.value) || null,
      loadDateToUpdate: this.convertToUTC(this.loadDateToUpdate.value) || null,
      purchaseOrderToUpdate: this.purchaseOrderToUpdate.value || null
    };

    this.updateOrderService
      .updateOrder(String(this.data._id), dataToUpdate)
      .subscribe({
        next: (res) => {
          this.snackBar.open(
            this.data.type.code == DocumentTypeEnum.COTIZACION
              ? "Cotización actualizada con éxito."
              : "Pedido actualizado con éxito.",
            "Cerrar",
            {
              duration: 3000,
            }
          );
          this.isLoading = false;
          this.dialogRef.close(res);
        },
        error: (err) => {
          this.isLoading = false;
          this.snackBar.open(
            err.error.message || this.data.type.code == DocumentTypeEnum.COTIZACION
              ? "Error al actualizar la cotización."
              : "Error al actualizar el pedido.",
            "Cerrar",
            { duration: 3000 }
          );
        },
      });
  }

  private convertToUTC(date: any): string | null {
    if (!date) return null;

    let dateObj: Date;

    if (typeof date === 'string') {
      const parsed = new Date(date);
      if (isNaN(parsed.getTime())) return null;
      dateObj = parsed;
    }

    else if (date instanceof Date) {
      if (isNaN(date.getTime())) return null;
      dateObj = date;
    }

    else if (typeof date === 'object' && 'year' in date && 'month' in date && 'day' in date) {
      dateObj = new Date(date.year, date.month - 1, date.day);
    }

    else {
      return null;
    }

    return dateObj.toISOString(); // UTC con 'Z'
  }


}
